import React, { useEffect, useState } from "react";
import {
    Chat,
    InactiveChannelBanner,
} from "./chatStyled";
import { VipUploadNote } from "./chatStyled";
import InsideHeader from "./components/insideHeader";
import ChatBody from "./components/chatBody";
import ChatBodyFooter from "./components/chatBodyFooter";
import InputMessage from "./components/inputMessage";
import { SVGBlackSendIcon, SVGSendMessage, SVGSwingUploadIcon, SVGUploadFileIcon } from "./icons";
import { useSendbirdUser } from "./sendbird.hooks";
import { useMediaQuery } from "@mui/material";
import ChatBodyLoad from "./components/chatBodyLoad";
import { Member } from "@sendbird/chat/groupChannel";
import ChatHeader from "./components/chatHeader";
import { pgVIPViewed, pgVIPNewMessage, pgVIPVideoUpload, pgVIPCoachShowChat } from "../mixPanelEvents/mixPanelEvents";
import FileSizeErrorModal from "../fileSizeErrorModal";

export interface SendbirdChatProps {
    pageTitle?: string;
    user: SendbirdUser | null;
    coach: Member | null;
    setCoach: (coach: Member | null) => void;
    anyMessageSent: boolean;
    setAnyMessageSent: (sent: boolean) => void;
    isFreemium?: boolean;
    isInactiveChannel?: boolean;
    setView?: (view: 'init' | 'chat') => void;
}

export interface ChatMessage {
    id: number;
    message: string;
    sender_id: string;
    created_at?: Date;
    type?: string; // e.g., "user", "file", etc.
    raw?: any; // to hold the original message object from Sendbird, if needed for future features (like editing, reactions, etc.)
}

export interface SendbirdUser {
    id?: number;

    user_id: string;
    access_token: string;
    // subscriber fields
    channel_url: string;
    was_member?: number;
    vip_coaching_second_autoresponder_message?: string;
    // coach fields
    unassigned_channels?: { channel_url: string; created_at: number; user: { nickname: string; user_id: string; profile_url: string } }[];
    flags?: {
        duesoon: number;
        overdue: number;
    };
    user_role?: 'coach' | 'subscriber' | 'support';


    //Support page fields
    first_name?: string;
    last_name?: string;
    email?: string;
    sendbird_id?: string;
    userFlags?: UserFlag[];
    support_channel?: string;
}

export type UserFlag = 'vip' | 'vip_elite' | 'vip_experience';

const SendbirdChat: React.FC<SendbirdChatProps> = (props) => {
    const { 
        user,
        channel,
        sendbirdMessages, 
        setSendbirdMessages, 
        sendUserMessage,
        sendFileMessage,
        updateUserMessage,
        deleteMessage, 
        numberOfVideos,
        markAsRead,
        userLoading, 
        userError 
    } = useSendbirdUser(props);

    useEffect(() => {
        if (channel) {
            markAsRead();
            if (props.user?.user_role === 'coach') {
                pgVIPCoachShowChat({});
            } else if (props.user?.user_role !== 'subscriber') {
                pgVIPViewed('Messages From Coach');
            }
        }
    }, [channel]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [currentEditingMessageId, setCurrentEditingMessageId] = useState<number | null>(null);
    const [fileToUpload, setFileToUpload] = useState<File | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [paywallOpen, setPaywallOpen] = useState(false);
    const [fileSizeErrorOpen, setFileSizeErrorOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:768px)');
    const xsDevice = useMediaQuery('(max-width:420px)');
    const isVipCoachPage = props.pageTitle === 'VIP Coaching' || props.pageTitle === 'Support Chat';
    
    // Block when user is freemium AND has more than 1 video
    const isBlocked = (props.isFreemium ?? false) && (numberOfVideos ?? 0) > 1;
    //  const isBlocked = (numberOfVideos ?? 0) > 1;
     console.log("number of videos!!!! >> ", isBlocked);
    const onSend = (message: string) => {
        if (isSending) return;
        setIsSending(true);

    if (message.trim() !== "" && currentEditingMessageId === null) {
            sendUserMessage(user!, message)
                .then((sentMessage) => {
                    setSendbirdMessages(prev => [{
                        id: sentMessage.messageId,
                        message: sentMessage.message,
                        sender_id: sentMessage.sender?.userId || '',
                        created_at: new Date(sentMessage.createdAt),
                        raw: sentMessage
                    }, ...prev]);
                    // VIP Message Sent (text) – use same event as legacy VIP chat
                    pgVIPNewMessage({
                        Type: user?.user_role === 'coach' ? 'Coach' : user?.user_role === 'support' ? 'Support' : 'Member',
                    });
                })
                .catch((err) => {
                    console.error("Failed to send message:", err);
                })
                .finally(() => {
                    setIsSending(false);
                });
        }
        if (currentEditingMessageId !== null) {
            updateUserMessage(user!, currentEditingMessageId, message)
                .then((updatedMessage) => {
                    setSendbirdMessages(prev => prev.map(m => 
                        m.id === currentEditingMessageId 
                            ? { ...m, message: updatedMessage.message, raw: updatedMessage } 
                            : m
                    ));
                    setCurrentEditingMessageId(null);
                    setCurrentMessage('');
                })
                .catch((err) => {
                    console.error("Failed to update message:", err);
                })
                .finally(() => {
                    setIsSending(false);
                });
        }
        if (fileToUpload) {
            sendFileMessage(user!, fileToUpload)
                .then((sentMessage) => {
                    setSendbirdMessages(prev => [{
                        id: sentMessage.messageId,
                        message: sentMessage.message,
                        sender_id: sentMessage.sender?.userId || '',
                        created_at: new Date(sentMessage.createdAt),
                        type: sentMessage.messageType,
                        raw: sentMessage
                    }, ...prev]);
                    setFileToUpload(null);
                    const fileEventParams = {
                        'Video ID': sentMessage.messageId,
                        Type: user?.user_role === 'coach' ? 'Coach' : user?.user_role === 'support' ? 'Support' : 'Member',
                    };
                    pgVIPVideoUpload(fileEventParams);
                    pgVIPNewMessage(fileEventParams);
                })
                .catch((err) => {
                    console.error("Failed to send file message:", err);
                })
                .finally(() => {
                    setIsSending(false);
                });
        }
    }

    const onLoadFile = (file: File) => {
        const limitMb = (window as any).pgChat?.fileSizeLimit ?? 250;
        if (file.size > limitMb * 1024 * 1024) {
            setFileSizeErrorOpen(true);
            return;
        }
        setFileToUpload(file);
    }
    
    const handleBlockedAction = () => {
        setPaywallOpen(true);
    }

    const removeFileToUpload = () => {
        setFileToUpload(null);
    }

    const onEditMessage = (messageId: number, oldMessage: string) => {
        setCurrentEditingMessageId(messageId);
        setCurrentMessage(oldMessage);
    }


    return (
        <>
        <Chat $isVipPage={isVipCoachPage}>
            {isVipCoachPage ? (
                <ChatHeader user={user} channel={channel} coach={props.coach} isVipPage={isVipCoachPage} setView={props.setView} />
            ) : (
                <InsideHeader
                    Title="Get faster results"
                    SubTitle="Let's See Your Swing"
                    ButtonText="Upload Swing Video"
                    Message="THIS IS YOUR <span>PRIVATE</span> COACHING PORTAL. YOUR SWING VIDEOS ARE ONLY SHARED WITH YOUR COACH TO DIAL IN YOUR GAME. Please keep recordings under <span>1 minute</span> for fastest upload time."
                    ButtonIcon={SVGUploadFileIcon}
                    onLoadFile={onLoadFile}
                    user={user}
                    isBlocked={isBlocked}
                    onBlockedAction={handleBlockedAction}
                />
            )}
            
            {props.isInactiveChannel && (
                <InactiveChannelBanner>
                    This customer doesn't have an active VIP Coaching subscription and cannot view or reply to messages.
                </InactiveChannelBanner>
            )}
            {(user?.user_id && channel) ?
            <>
                <ChatBody 
                    messages={sendbirdMessages} 
                    user={user}
                    onEditMessage={onEditMessage}
                    deleteMessage={deleteMessage}
                    isMobile={isMobile}
                    isVipCoachPage={isVipCoachPage}
                    subscriberUserId={
                        channel?.members.find(m =>
                            m.userId !== user?.user_id
                            && m.userId.startsWith('vip_support_') === false
                            && m.userId.startsWith('vip_coach') === false
                            && m.userId !== 'autoresponder'
                            && m.userId !== 'admin'
                        )?.userId
                    }
                />

                {!isVipCoachPage && 
                    <ChatBodyFooter 
                        SwingUploadIcon={SVGSwingUploadIcon} 
                        SwingUploadTypo="Upload Swing Video" 
                        SwingUploadMessage="*Please keep recordings under 1 minute for fastest upload time." 
                        onLoadFile={onLoadFile}
                        isBlocked={isBlocked}
                        onBlockedAction={handleBlockedAction}
                    />
                }

                <InputMessage 
                    Placeholder={(xsDevice || isVipCoachPage) ? "Start typing..." : "Type your message here..."}
                    SendMessageIcon={(xsDevice || isVipCoachPage) ? SVGBlackSendIcon : SVGSendMessage}
                    UserVideoNum={numberOfVideos}
                    CurrentMessage={currentMessage}
                    setCurrentMessage={setCurrentMessage}
                    FileToUpload={fileToUpload}
                    onSend={onSend}
                    removeFileToUpload={removeFileToUpload}
                    isSending={isSending}
                    isBlocked={isBlocked}
                    onBlockedAction={handleBlockedAction}
                    isCoach={user?.user_role === 'coach'}
                    isVipCoachPage={isVipCoachPage}
                    onLoadFile={isVipCoachPage ? onLoadFile : undefined}
                />
            </>
            : <ChatBodyLoad isVipPage={isVipCoachPage} />}
        </Chat>

        {(isVipCoachPage && user?.user_role === 'subscriber') && 
            <VipUploadNote>*Please keep recordings under 1 minute for fastest upload time.</VipUploadNote>
        }
        <FileSizeErrorModal
            isOpen={fileSizeErrorOpen}
            onClose={() => setFileSizeErrorOpen(false)}
            page="Chat"
            type={user?.user_role === 'coach' ? 'Coach' : 'Member'}
        />
        </>
    );
}

export default SendbirdChat;